import { appendBrainLoader, stripBrainLoader } from "./brain-loader-block.js";
import { hasA2aSnippet, appendSnippet } from "./a2a-snippet.js";
import type { AgentVersionRecord, AgentDefinition } from "./foundry-agent-get.js";

/** Recompose deployed instructions from a new base body, preserving whatever overlays the live agent carried. */
export function composeInstructions(args: {
  baseBody: string;
  current: AgentVersionRecord;
  loaderText: string | null;
  brainRepo: string | null;
}): string {
  const live = args.current.definition.instructions ?? "";
  let out = args.baseBody;
  const hadLoader = stripBrainLoader(live) !== live;
  if (hadLoader && args.loaderText) {
    const templated = args.brainRepo ? args.loaderText.replaceAll("{{brain_repo}}", args.brainRepo) : args.loaderText;
    out = appendBrainLoader(out, templated);
  }
  if (hasA2aSnippet(live)) out = appendSnippet(out);
  return out;
}

export function resolveFillableValues(
  current: AgentVersionRecord,
  yamlValues: Record<string, string> | null,
): Record<string, string> | null {
  const raw = current.metadata?.fillableFieldValues;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, string>;
    } catch {
      /* fall through */
    }
  }
  return yamlValues;
}

/** True when re-versioning would produce an identical agent (skip createVersion). */
export function isNoop(
  candidate: { definition: AgentDefinition; metadata: Record<string, string> },
  current: AgentVersionRecord,
): boolean {
  const a = candidate.definition.instructions ?? "";
  const b = current.definition.instructions ?? "";
  if (a !== b) return false;
  if (JSON.stringify(candidate.definition.tools ?? []) !== JSON.stringify(current.definition.tools ?? [])) return false;
  return JSON.stringify(sortObj(candidate.metadata)) === JSON.stringify(sortObj(current.metadata ?? {}));
}

function sortObj(o: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(o).sort(([x], [y]) => x.localeCompare(y)));
}
